/**
 * MouvementsPanel — the intra-session CaisseMouvement ledger (entrées / sorties) + the cash-out.
 *
 * The ledger rows come from the X-report's drawer state; the per-row list is read from
 * GET /finance/caisse/:id/x-report's source — but since the server exposes the rows only via the
 * X-report count, the visible list here is the corrections-style append log we keep from successful
 * adds plus a manual refresh. A `sortie` is server-guarded against the live drawer (we surface the
 * 409 inline). Cancelling a movement posts an offsetting `annule` (L-1 — never a delete).
 *
 * NOTE: the server has no list-mouvements endpoint exposed under /api/v1 for the client, so the
 * ledger view is built from this session's adds/cancels in this surface; the authoritative count +
 * theoretical impact is always reflected by the X-report (which IS server-read).
 */
import { useCallback, useState, type FormEvent } from "react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  Modal,
  Select,
  Textarea,
  Table,
  useToast,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { token } from "@/design/theme";
import { FINANCE_CAPS, useAddMouvement, useCancelMouvement } from "../api";
import type { CaisseMouvement, Sens } from "../types";
import { MoneyInput } from "./MoneyInput";
import { Money } from "./Money";

export interface MouvementsPanelProps {
  labId: string | null;
  sessionId: string;
  /** Refresh the X-report after a ledger change (drawer balance moved). */
  onChanged: () => void;
}

let localSeq = 0;

export function MouvementsPanel({ labId, sessionId, onChanged }: MouvementsPanelProps) {
  const { t } = useT();
  const toast = useToast();
  const canCreate = useCan(FINANCE_CAPS.mouvementCreate);

  const addMut = useAddMouvement(labId, sessionId);
  const cancelMut = useCancelMouvement(labId, sessionId);

  // Session-local ledger built from this surface's successful adds (the server count is the X-report).
  const [rows, setRows] = useState<CaisseMouvement[]>([]);

  const [addOpen, setAddOpen] = useState(false);
  const [sens, setSens] = useState<Sens>("entree");
  const [montant, setMontant] = useState<number | null>(null);
  const [motif, setMotif] = useState("");
  const [reference, setReference] = useState("");
  const [touched, setTouched] = useState(false);

  const [cancelTarget, setCancelTarget] = useState<CaisseMouvement | null>(null);
  const [cancelMotif, setCancelMotif] = useState("");
  const [cancelTouched, setCancelTouched] = useState(false);

  const montantError = touched && (montant == null || montant <= 0) ? t("finance.field.amountInvalid") : null;
  const motifError = touched && motif.trim().length < 2 ? t("finance.field.motifShort") : null;
  const addValid = montant != null && montant > 0 && motif.trim().length >= 2;

  const resetAdd = () => {
    setSens("entree");
    setMontant(null);
    setMotif("");
    setReference("");
    setTouched(false);
  };

  // Stable close handlers — the UI-kit Modal's focus-trap effect re-runs on a new onClose identity,
  // which would steal focus from a field mid-typing. useCallback keeps them referentially stable.
  const closeAdd = useCallback(() => {
    setSens("entree");
    setMontant(null);
    setMotif("");
    setReference("");
    setTouched(false);
    setAddOpen(false);
  }, []);

  const closeCancel = useCallback(() => {
    setCancelTarget(null);
    setCancelMotif("");
    setCancelTouched(false);
  }, []);

  const submitAdd = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!addValid || !canCreate) return;
    addMut.mutate(
      {
        sens,
        montant: montant ?? 0,
        motif: motif.trim(),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
      },
      {
        onSuccess: (data) => {
          toast.success(t("finance.mvt.success"));
          setRows((prev) => [
            ...prev,
            {
              id: data.mouvement_id,
              sens: data.sens,
              montant: data.montant,
              motif: motif.trim(),
              reference: reference.trim() || null,
              statut: "valide",
              _local: ++localSeq,
            } as CaisseMouvement & { _local: number },
          ]);
          resetAdd();
          setAddOpen(false);
          onChanged();
        },
        onError: (err) => {
          // The cash-out guard surfaces a 409 with code FINANCE_CASH_OUT_DEPASSE.
          const msg =
            err instanceof ApiError
              ? err.code === "FINANCE_CASH_OUT_DEPASSE"
                ? t("finance.mvt.cashOutExceeds")
                : err.message
              : t("finance.mvt.title");
          toast.error(msg);
        },
      },
    );
  };

  const cancelMotifError =
    cancelTouched && cancelMotif.trim().length < 2 ? t("finance.field.motifShort") : null;

  const submitCancel = (e: FormEvent) => {
    e.preventDefault();
    setCancelTouched(true);
    if (!cancelTarget || cancelMotif.trim().length < 2 || !canCreate) return;
    cancelMut.mutate(
      { mouvementId: String(cancelTarget.id), motif: cancelMotif.trim() },
      {
        onSuccess: (data) => {
          toast.success(t("finance.mvt.cancel.success"));
          setRows((prev) =>
            prev.map((r) =>
              String(r.id) === String(cancelTarget.id)
                ? { ...r, statut: data.statut, annulation_motif: cancelMotif.trim() }
                : r,
            ),
          );
          setCancelTarget(null);
          setCancelMotif("");
          setCancelTouched(false);
          onChanged();
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : t("finance.mvt.cancel.title"));
        },
      },
    );
  };

  const columns: ReadonlyArray<Column<CaisseMouvement>> = [
    {
      key: "sens",
      header: t("finance.mvt.sens"),
      cell: (row) => (
        <Badge tone={row.sens === "entree" ? "success" : "warn"}>
          {row.sens === "entree" ? t("finance.mvt.sens.entree") : t("finance.mvt.sens.sortie")}
        </Badge>
      ),
    },
    {
      key: "montant",
      header: t("finance.mvt.montant"),
      align: "end",
      mono: true,
      cell: (row) => <Money centimes={row.sens === "sortie" ? -row.montant : row.montant} signed />,
    },
    {
      key: "motif",
      header: t("finance.mvt.motif"),
      cell: (row) => <span>{row.motif}</span>,
    },
    {
      key: "statut",
      header: t("finance.mvt.statut"),
      cell: (row) =>
        row.statut === "annule" ? (
          <Badge tone="neutral">{t("finance.mvt.statut.annule")}</Badge>
        ) : (
          <Badge tone="info">{t("finance.mvt.statut.valide")}</Badge>
        ),
    },
    {
      key: "actions",
      header: t("finance.06.actions"),
      align: "end",
      width: "120px",
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          disabled={!canCreate || row.statut !== "valide"}
          onClick={() => {
            setCancelTarget(row);
            setCancelMotif("");
            setCancelTouched(false);
          }}
        >
          {t("finance.mvt.cancel")}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <Card
        title={t("finance.mvt.title")}
        actions={
          <Button size="sm" disabled={!canCreate} onClick={() => setAddOpen(true)}>
            + {t("finance.mvt.add")}
          </Button>
        }
      >
        <p style={{ margin: "0 0 var(--nv-s-3)", color: token.slate, fontSize: "var(--nv-fs-label)" }}>
          {t("finance.mvt.subtitle")}
        </p>
        {rows.length === 0 ? (
          <EmptyState
            glyph="▤"
            title={t("finance.mvt.empty.title")}
            description={t("finance.mvt.empty.desc")}
          />
        ) : (
          <Table<CaisseMouvement>
            columns={columns}
            rows={rows}
            rowKey={(r) => String((r as CaisseMouvement & { _local?: number })._local ?? r.id)}
            caption={t("finance.mvt.title")}
          />
        )}
      </Card>

      {/* Add movement */}
      <Modal
        open={addOpen}
        onClose={closeAdd}
        title={t("finance.mvt.add")}
        footer={
          <>
            <Button variant="ghost" onClick={closeAdd} disabled={addMut.isPending}>
              {t("action.close")}
            </Button>
            <Button type="submit" form="finance-add-mvt" loading={addMut.isPending} disabled={!canCreate}>
              {t("finance.mvt.submit")}
            </Button>
          </>
        }
      >
        <form id="finance-add-mvt" onSubmit={submitAdd} noValidate>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <FormField label={t("finance.mvt.sens")}>
              {(p) => (
                <Select
                  {...p}
                  value={sens}
                  onChange={(e) => setSens(e.target.value as Sens)}
                  options={[
                    { value: "entree", label: t("finance.mvt.sens.entree") },
                    { value: "sortie", label: t("finance.mvt.sens.sortie") },
                  ]}
                />
              )}
            </FormField>

            <FormField
              label={t("finance.mvt.montant")}
              required
              {...(montantError ? { error: montantError } : {})}
            >
              {(p) => (
                <MoneyInput
                  {...p}
                  autoFocus
                  aria-label={t("finance.amount.aria")}
                  valueCentimes={montant}
                  onChangeCentimes={setMontant}
                  invalid={!!montantError}
                />
              )}
            </FormField>

            <FormField
              label={t("finance.mvt.motif")}
              hint={t("finance.mvt.motif.hint")}
              required
              {...(motifError ? { error: motifError } : {})}
            >
              {(p) => (
                <Input
                  {...p}
                  autoComplete="off"
                  value={motif}
                  invalid={!!motifError}
                  onChange={(e) => setMotif(e.target.value)}
                />
              )}
            </FormField>

            <FormField label={t("finance.mvt.reference")} hint={t("finance.mvt.reference.hint")}>
              {(p) => (
                <Input
                  {...p}
                  mono
                  className="nv-ltr-island"
                  autoComplete="off"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              )}
            </FormField>
          </div>
        </form>
      </Modal>

      {/* Cancel movement */}
      <Modal
        open={!!cancelTarget}
        onClose={closeCancel}
        title={t("finance.mvt.cancel.title")}
        footer={
          <>
            <Button variant="ghost" onClick={closeCancel} disabled={cancelMut.isPending}>
              {t("action.close")}
            </Button>
            <Button
              type="submit"
              form="finance-cancel-mvt"
              variant="danger"
              loading={cancelMut.isPending}
              disabled={!canCreate}
            >
              {t("finance.mvt.cancel.submit")}
            </Button>
          </>
        }
      >
        <form id="finance-cancel-mvt" onSubmit={submitCancel} noValidate>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <p
              role="note"
              style={{
                margin: 0,
                padding: "var(--nv-s-3)",
                borderRadius: "var(--nv-r-md)",
                background: token.tintHigh,
                color: token.warn,
                fontSize: "var(--nv-fs-label)",
              }}
            >
              ▲ {t("finance.mvt.cancel.warn")}
            </p>
            <FormField
              label={t("finance.mvt.cancel.motif")}
              required
              {...(cancelMotifError ? { error: cancelMotifError } : {})}
            >
              {(p) => (
                <Textarea
                  {...p}
                  autoFocus
                  rows={2}
                  value={cancelMotif}
                  invalid={!!cancelMotifError}
                  onChange={(e) => setCancelMotif(e.target.value)}
                />
              )}
            </FormField>
          </div>
        </form>
      </Modal>
    </div>
  );
}
